import React, { useState, useEffect, useContext } from "react";
import { Bar, Pie, Line, Doughnut, Radar } from "react-chartjs-2";
import { Row, Col, Card, Form, Modal, Button } from "react-bootstrap";
import { useSpring, animated, config } from "@react-spring/web";
import axios from "axios";
import { getNextRenewalDate } from "../../../utils/renewalUtils";
import Preloader from "../../../shared/PreLoaders";
import { Authorization } from "../../../Context/Authorization";
const VFCDashboard = () => {
  const [showModal, setShowModal] = useState(true);
  const [circles, setCircles] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [ranges, setRanges] = useState([]);
  const [vfcs, setVfcs] = useState([]);
  const [animate, setAnimate] = useState(false);
  const [viewCharts, setViewCharts] = useState(false);
  const [notificationContent, setNotificationContent] = useState([]);
  const [selectedCircle, setSelectedCircle] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedRange, setSelectedRange] = useState("");
  const [selectedVfc, setSelectedVfc] = useState("");
  const [state] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const Api = process.env.REACT_APP_API;
  const [selectedVfcData, setSelectedVfcData] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);

  const COLORS = [
    "#FF6384",
    "#36A2EB",
    "#FFCE56",
    "#4BC0C0",
    "#9966FF",
    "#FF9F40",
    "#C9CBCF",
    "#FF6666",
    "#66FF66",
    "#6666FF",
  ];

  useEffect(() => {
    setLoading(true);
    const fetchCircle = async () => {
      try {
        const config = Authorization();
        const response = await axios.get(`${Api}/api/circles`, config);
        const data = response.data;
        setCircles(data);
      } catch (err) {
        setErrorMessage("No circles available.");
        console.error("Error fetching circles", err);
      }
    };

    const fetchDivision = async () => {
      try {
        const config = Authorization();
        const response = await axios.post(
          `${Api}/api/divisions/divisions-by-circle`,

          { circleId: selectedCircle },
          config
        );

        const data = response.data;
        setDivisions(data);
      } catch (err) {
        setErrorMessage("No Divisions available.");
        console.error("Error fetching divisions", err);
      }
    };

    const fetchRange = async () => {
      try {
        const config = Authorization();
        const response = await axios.post(
          `${Api}/api/ranges/ranges-by-division`,

          { divisionId: selectedDivision },
          config
        );

        const data = response.data;
        setRanges(data);
      } catch (err) {
        setErrorMessage("No Range available.");
        console.error("Error fetching ranges", err);
      }
    };

    const fetchVfc = async () => {
      try {
        const config = Authorization();
        const response = await axios.post(
          `${Api}/api/vfcs/vfcs-by-range`,

          {
            rangeId: selectedRange,
          },
          config
        );
        const data = response.data.vfcs;
        setVfcs(data);
        setSelectedVfcData(data);
        setSelectedVfc(data);
      } catch (err) {
        console.error("Error fetching VFC data", err);
        setErrorMessage("No VFC available.");
      }
    };
    fetchCircle();
    if (selectedCircle) fetchDivision();
    if (selectedDivision) fetchRange();
    if (selectedRange) fetchVfc();
    setLoading(false);
  }, [Api, selectedCircle, selectedDivision, selectedRange]);

  useEffect(() => {
    if (selectedVfc) {
      const vfc = selectedVfcData.find((vfc) => vfc._id === selectedVfc);
      if (vfc) {
        const nextRenewalDate = getNextRenewalDate(
          new Date(vfc.dateOfEstablishment)
        );
        setNotificationContent([
          `Renewal is due for ${
            vfc.vfcName
          } on ${nextRenewalDate.toLocaleDateString()}`,
        ]);
      } else {
        setNotificationContent([]);
      }
    } else {
      setNotificationContent([]);
    }
  }, [selectedVfc, selectedVfcData]);

  const fetchDashboardData = async () => {
    try {
      const config = Authorization();
      const response = await axios.post(
        `${Api}/api/vfcs/vfc-dashboard-data`,
        {
          circleId: selectedCircle,
          divisionId: selectedDivision,
          rangeId: selectedRange,
          vfcId: selectedVfc,
        },
        config
      );

      setDashboardData(response.data);
    } catch (err) {
      console.error("Failed to fetch dashboard chart data", err);
    }
  };

  const handleModalClose = () => setShowModal(false);
  const totalFamilies = vfcs.reduce((acc, vfc) => vfc.totalFamily, 0);
  const totalMembers = vfcs.reduce((acc, vfc) => acc + vfc.totalMembers, 0);
  const totalSHG = vfcs.reduce((acc, vfc) => acc + vfc.totalSHG, 0);
  const totalLoan = vfcs.reduce((acc, vfc) => acc + vfc.totalLoanAmount, 0);
  // Bar Chart: Year-wise Loan Issued
  const barData = {
    labels: dashboardData?.yearWiseLoanData.labels || [],
    datasets: [
      {
        label: "Loan Issued",
        data: dashboardData?.yearWiseLoanData.loanIssued || [],
        backgroundColor: COLORS[0],
      },
    ],
  };

  // Line Chart: Monthly Loan Issued and Repayments
  const lineData = {
    labels: dashboardData?.monthlyLoanData.labels || [],
    datasets: [
      {
        label: "Loan Issued",
        data: dashboardData?.monthlyLoanData.loanIssued || [],
        borderColor: "#4BC0C0",
        fill: false,
        tension: 0.4,
      },
      {
        label: "Repayments",
        data: dashboardData?.monthlyLoanData.repayments || [],
        borderColor: "#FF6384",
        fill: false,
        tension: 0.4,
      },
    ],
  };

  const performanceScale = [
    "Needs Improvement",
    "Below Average",
    "Moderately Performing",
    "High Performing",
  ];
  const performanceColors = ["#e74c3c", "#e67e22", "#f1c40f", "#2ecc71"];

  const performanceIndex = performanceScale.indexOf(
    dashboardData?.performanceStatus ?? ""
  );

  const performanceBarData = {
    labels: performanceScale,
    datasets: [
      {
        label: "Performance Status",
        data: performanceScale.map((_, idx) =>
          idx === performanceIndex ? 1 : 0
        ),
        backgroundColor: performanceScale.map((_, idx) =>
          idx === performanceIndex ? performanceColors[idx] : "#ddd"
        ),
      },
    ],
  };

  const loanBalance = dashboardData?.loanBalance || 0;
  const repaymentAmount = totalLoan - loanBalance;

  const doughnutData = {
    labels: ["Loan Amount", "Recovered Amount", "Loan Balance"],
    datasets: [
      {
        data: [totalLoan, repaymentAmount, loanBalance],
        backgroundColor: ["#36A2EB", "#4BC0C0", "#FF6384"],
      },
    ],
  };

  // Radar Chart: Multiple attributes per VFC
  const vfcDetails = dashboardData?.vfcDataWithDetails || [];

  const totalMember = vfcDetails.reduce(
    (sum, vfc) => sum + (vfc.totalMembers || 0),
    0
  );
  const totalShg = vfcDetails.reduce(
    (sum, vfc) => sum + (vfc.totalSHG || 0),
    0
  );
  const totalLoanAmount = vfcDetails.reduce(
    (sum, vfc) => sum + (vfc.totalLoanAmount || 0),
    0
  );

  // Find dynamic maximums across the dataset
  const maxMember = Math.max(...vfcDetails.map((v) => v.totalMembers || 0), 1); // Avoid divide-by-zero
  const maxShg = Math.max(...vfcDetails.map((v) => v.totalSHG || 0), 1);
  const maxLoanAmount = Math.max(
    ...vfcDetails.map((v) => v.totalLoanAmount || 0),
    1
  );

  // Normalize data to 0–100 scale
  const radarData = {
    labels: ["Members", "SHG", "Loan Amount"],
    lable: [dashboardData?.vfcs.vfcRating],
    datasets: [
      {
        label:
          dashboardData?.vfcs?.length === 1
            ? dashboardData.vfcs[0].vfcName
            : "Combined VFCs",
        data: [
          (totalMember / maxMember) * 100,
          (totalShg / maxShg) * 100,
          (totalLoanAmount / maxLoanAmount) * 100,
        ],
        backgroundColor: "rgba(54, 162, 235, 0.2)",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 1,
      },
    ],
  };

  const notificationAnimation = useSpring({
    transform: animate ? "translateX(0%)" : "translateX(100%)",
    opacity: animate ? 1 : 0,
    backgroundColor: animate ? "#ffeb3b" : "#fff176",
    scale: animate ? 1.05 : 1,
    config: config.stiff,
  });

  const colorPulse = useSpring({
    loop: true,
    to: { backgroundColor: "#dc3545" },
    from: { backgroundColor: "#ffeb3b" },
    config: { duration: 1000 },
  });

  const handleViewClick = () => {
    if (selectedRange && selectedVfc) {
      fetchDashboardData();
      setViewCharts(true);
      setAnimate(true);
    } else {
      setViewCharts(false);
    }
  };

  return (
    <div>
      <Preloader loading={loading} />
      <Modal show={showModal} onHide={handleModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Select VFC Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Circle</Form.Label>
              <Form.Control
                as="select"
                value={selectedCircle}
                onChange={(e) => setSelectedCircle(e.target.value)}
              >
                <option value="">Select Circle</option>
                {circles.map((circle) => (
                  <option key={circle._id} value={circle._id}>
                    {circle.circleName}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Division</Form.Label>
              <Form.Control
                as="select"
                value={selectedDivision}
                onChange={(e) => setSelectedDivision(e.target.value)}
                disabled={!selectedCircle}
              >
                <option value="">Select Division</option>
                {divisions.map((division) => (
                  <option key={division._id} value={division._id}>
                    {division.divisionName}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Range</Form.Label>
              <Form.Control
                as="select"
                value={selectedRange}
                onChange={(e) => setSelectedRange(e.target.value)}
                disabled={!selectedDivision}
              >
                <option value="">Select Range</option>
                {ranges.map((range) => (
                  <option key={range._id} value={range._id}>
                    {range.rangeName}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>VFC</Form.Label>
              <Form.Control
                as="select"
                value={selectedVfc}
                onChange={(e) => setSelectedVfc(e.target.value)}
                disabled={!selectedRange}
              >
                <option value="">Select VFC</option>
                {vfcs.map((vfc) => (
                  <option key={vfc._id} value={vfc._id}>
                    {vfc.vfcName}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="submit"
            className="btn btn-outline-primary btn-icon-text mt-1"
            onClick={() => {
              handleModalClose();
              handleViewClick();
            }}
            disabled={!selectedVfc}
          >
            <i className="mdi mdi-file-check btn-icon-prepend"></i>
            Submit
          </button>
          <button
            className="btn btn-outline-dark mt-3"
            onClick={handleModalClose}
          >
            Cancel
          </button>
        </Modal.Footer>
        {errorMessage && (
          <div className="alert alert-danger mt-3" role="alert">
            {errorMessage}
          </div>
        )}
      </Modal>
      {viewCharts && (
        <>
          <Preloader loading={loading} />
          <div className="page-header">
            <h3 className="page-title"> VFC Dashboard </h3>

            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a href="!#" onClick={(event) => event.preventDefault()}>
                    VFC
                  </a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  VFC Dashboard
                </li>
              </ol>
            </nav>
          </div>

          <Row>
            <Col xs={6}>
              <div className="row">
                <div className="col-md-6 stretch-card grid-margin">
                  <div className="card bg-gradient-danger card-img-holder text-white">
                    <div className="card-body">
                      <img
                        src={require("../../../../assets/images/dashboard/circle.png")}
                        className="card-img-absolute"
                        alt="circle"
                      />
                      <h4 className="font-weight-normal mb-3">
                        Total Family
                        <i className="mdi mdi-chart-line mdi-24px float-right"></i>
                      </h4>
                      <h2 className="mb-5">{totalFamilies}</h2>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 stretch-card grid-margin">
                  <div className="card bg-gradient-info card-img-holder text-white">
                    <div className="card-body">
                      <img
                        src={require("../../../../assets/images/dashboard/circle.png")}
                        className="card-img-absolute"
                        alt="circle"
                      />
                      <h4 className="font-weight-normal mb-3">
                        Total Members
                        <i className="mdi mdi-bookmark-outline mdi-24px float-right"></i>
                      </h4>
                      <h2 className="mb-5">{totalMembers}</h2>
                    </div>
                  </div>
                </div>
              </div>
              <Row>
                <div className="col-md-6 stretch-card grid-margin">
                  <div className="card bg-gradient-danger card-img-holder text-white">
                    <div className="card-body">
                      <img
                        src={require("../../../../assets/images/dashboard/circle.png")}
                        className="card-img-absolute"
                        alt="circle"
                      />
                      <h4 className="font-weight-normal mb-3">
                        Total SHG
                        <i className="mdi mdi-chart-line mdi-24px float-right"></i>
                      </h4>
                      <h2 className="mb-5">{totalSHG}</h2>
                    </div>
                  </div>
                </div>

                <div className="col-md-6 stretch-card grid-margin">
                  <div className="card bg-gradient-success card-img-holder text-white">
                    <div className="card-body">
                      <img
                        src={require("../../../../assets/images/dashboard/circle.png")}
                        className="card-img-absolute"
                        alt="circle"
                      />
                      <h4 className="font-weight-normal mb-3">
                        Total Loan
                        <i className="mdi mdi-diamond mdi-24px float-right"></i>
                      </h4>
                      <h2 className="mb-5">
                        <i className="mdi mdi-currency-inr">
                          {totalLoan.toFixed(2)}
                        </i>
                      </h2>
                    </div>
                  </div>
                </div>
              </Row>
            </Col>
            <Col xs={6} className="notification-container">
              <animated.div
                style={{
                  ...notificationAnimation,
                  ...colorPulse,
                  padding: "10px",
                  borderRadius: "5px",
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                }}
                className="notification-div"
              >
                {notificationContent.length > 0 ? (
                  notificationContent.map((msg, index) => (
                    <div key={index}>{msg}</div>
                  ))
                ) : (
                  <p>No notifications at the moment</p>
                )}
              </animated.div>
            </Col>
          </Row>

          <Row>
            <Col xs={6}>
              <h4>Your VFC Ratting</h4>
              <Radar data={radarData} />
            </Col>
            <Col xs={6}>
              <h4>Loan Status</h4>
              <Doughnut data={doughnutData} />
            </Col>
          </Row>

          <Row>
            <Col xs={6}>
              <h4>Loan Issue & Loan Repayment</h4>
              <Bar data={barData} />
            </Col>
            <Col xs={6}>
              <h4>Performance Evaluation</h4>
              <Pie data={performanceBarData} />
            </Col>
          </Row>
          <h4>Loan Issue Graph</h4>

          <Line data={lineData} />
        </>
      )}
    </div>
  );
};

export default VFCDashboard;
